<template>
  <div>
    <PageSpinner :onloader="onloader" />
    <base-header type="gradient-info" class="pb-6 pb-8 pt-5 pt-md-8">
      <router-link to="/dashboard" class="text-white">Dashboard</router-link>
      <router-link to="/school/admission" class="text-white"
        ><i class="fa fa-ellipsis-v px-2"></i>Admission Management</router-link
      >
      <span class="text-white"
        ><i class="fa fa-ellipsis-v px-2"></i>Online Admission</span
      >
    </base-header>
    <main class="container-fluid mt--7 admission-shell">
      <header class="page-heading">
        <div>
          <span class="eyebrow">ADMISSIONS</span>
          <h1>Admission applications</h1>
          <p>Create, review and update applicant records.</p>
        </div>
        <button class="btn btn-light" @click="startCreate" :disabled="saving">
          + New application
        </button>
      </header>
      <section class="panel mb-4">
        <div class="toolbar">
          <label for="registered-term"
            >Registered Term
            <span v-if="termsLoading" class="select-loader" aria-live="polite"
              ><i class="select-spinner"></i>Loading</span
            ></label
          ><select
            id="registered-term"
            v-model.number="selectedTermId"
            :disabled="termsLoading"
            :aria-busy="termsLoading"
          >
            <option :value="0">
              {{
                termsLoading
                  ? "Loading registered terms…"
                  : "Select registered term"
              }}
            </option>
            <option
              v-for="term in registeredTerms"
              :key="term.registeredTermId"
              :value="Number(term.registeredTermId)"
            >
              {{ term.schSession }} - {{ term.schTerm }}
            </option></select
          ><div class="record-tools">
            <label class="sr-only" for="application-search">
              Search applications
            </label>
            <input
              id="application-search"
              v-model.trim="searchQuery"
              type="search"
              class="application-search"
              placeholder="Search applicants…"
              :disabled="!selectedTermId || listLoading || applications.length === 0"
            />
            <span v-if="listLoading" class="loading-label" role="status">
              <i class="select-spinner"></i> Loading applications…
            </span>
            <span
              v-else-if="selectedTermId && applications.length"
              class="muted record-count"
            >
              {{ filteredApplications.length }} of {{ applications.length }} applications
            </span>
          </div>
        </div>
        <div v-if="listError" class="alert alert-danger">{{ listError }}</div>
        <div v-else-if="listLoading" class="empty loading-state" role="status">
          <i class="select-spinner"></i> Loading admission applications…
        </div>
        <div
          v-else-if="
            selectedTermId && applications.length === 0
          "
          class="empty"
        >
          No admission applications were found for this registered term.
        </div>
        <div v-else-if="!selectedTermId" class="empty">
          Select a registered term to view its applications.
        </div>
        <div v-else-if="filteredApplications.length === 0" class="empty">
          No applications match “{{ searchQuery }}”.
          <button
            class="btn btn-link btn-sm"
            type="button"
            @click="searchQuery = ''"
          >
            Clear search
          </button>
        </div>
        <div v-else class="application-list">
          <article
            v-for="row in filteredApplications"
            :key="row.admissionApplicationId"
            class="application-card"
          >
            <div class="application-card-heading">
              <img
                class="applicant-photo"
                :src="imageUrl(row.passportPhotoPath || row.PassportPhotoPath) || '/img/brand/empty.png'"
                :alt="`${row.firstName || ''} ${row.surname || ''}`.trim() || 'Applicant photo'"
              />
              <div class="applicant-heading-copy">
                <span class="application-reference">
                  {{ row.applicationReference || "Application" }}
                </span>
                <h2>
                  {{ [row.firstName, row.middleName, row.surname].filter(Boolean).join(" ") || "Applicant name unavailable" }}
                </h2>
              </div>
              <span class="status-badge" :class="statusClass(row.admissionStatus)">
                {{ row.admissionStatus || "Pending" }}
              </span>
            </div>
            <dl class="application-details">
              <div class="application-detail">
                <dt>Email</dt>
                <dd>{{ row.applicantEmail || "—" }}</dd>
              </div>
              <div class="application-detail">
                <dt>Gender</dt>
                <dd>{{ row.gender || "—" }}</dd>
              </div>
              <div class="application-detail">
                <dt>Class applied</dt>
                <dd>{{ row.className || "—" }}</dd>
              </div>
              <div class="application-detail">
                <dt>Registered term</dt>
                <dd>{{ row.sessionName || selectedTermLabel }}{{ row.termName ? ` - ${row.termName}` : "" }}</dd>
              </div>
              <div class="application-detail">
                <dt>Admission type</dt>
                <dd>{{ row.admissionType || "—" }}</dd>
              </div>
              <div class="application-detail">
                <dt>Date created</dt>
                <dd>{{ formatDate(row.dateCreated) }}</dd>
              </div>
            </dl>
            <footer class="application-card-actions">
              <button
                class="btn btn-sm btn-outline-primary"
                type="button"
                @click="editApplication(row)"
              >
                Edit application
              </button>
            </footer>
          </article>
        </div>
      </section>
      <modal
        :show.sync="formVisible"
        modal-classes="modal-dialog-centered modal-xl modal-dialog-scrollable"
        body-classes="overflow-auto"
        :show-close="!saving"
      >
        <div class="form-panel">
          <div class="panel-heading">
            <div>
              <span class="eyebrow">{{
                editingId ? "EDIT APPLICATION" : "NEW APPLICATION"
              }}</span>
              <h2>
                {{
                  editingId
                    ? "Update admission application"
                    : "Create admission application"
                }}
              </h2>
            </div>
            <button
              class="btn btn-sm btn-outline-secondary"
              @click="cancelForm"
              :disabled="saving"
            >
              Close
            </button>
          </div>
          <form ref="admissionForm" @submit.prevent="submitApplication">
            <div class="wizard-layout">
              <aside class="wizard-sidebar" aria-label="Application stages">
                <p class="wizard-label">APPLICATION STEPS</p>
                <button
                  v-for="(stage, index) in stages"
                  :key="stage.title"
                  type="button"
                  class="wizard-step"
                  :class="{
                    active: currentStage === index,
                    complete: currentStage > index,
                  }"
                  :aria-current="currentStage === index ? 'step' : null"
                  :disabled="saving || index > currentStage"
                  @click="goToStage(index)"
                >
                  <span class="step-number">{{
                    currentStage > index ? "✓" : index + 1
                  }}</span>
                  <span class="step-copy"
                    ><strong>{{ stage.title }}</strong
                    ><small>{{ stage.summary }}</small></span
                  >
                </button>
              </aside>
              <section ref="stagePanel" class="wizard-stage">
                <div class="stage-heading">
                  <span class="eyebrow"
                    >STEP {{ currentStage + 1 }} OF {{ stages.length }}</span
                  >
                  <h3>{{ stages[currentStage].title }}</h3>
                  <p>{{ stages[currentStage].description }}</p>
                </div>
                <div v-if="currentStage === 0" class="fields">
                  <div class="field">
                    <label
                      >Registered Term <b>*</b>
                      <span
                        v-if="termsLoading"
                        class="select-loader"
                        aria-live="polite"
                        ><i class="select-spinner"></i>Loading</span
                      ></label
                    ><select
                      v-model.number="form.registeredTermId"
                      required
                      :disabled="termsLoading"
                      :aria-busy="termsLoading"
                    >
                      <option value="">
                        {{
                          termsLoading
                            ? "Loading registered terms…"
                            : "Select registered term"
                        }}
                      </option>
                      <option
                        v-for="term in registeredTerms"
                        :key="term.registeredTermId"
                        :value="Number(term.registeredTermId)"
                      >
                        {{ term.schSession }} - {{ term.schTerm }}
                      </option>
                    </select>
                  </div>
                  <div class="field">
                    <label
                      >Class Applied For <b>*</b>
                      <span
                        v-if="classLoading"
                        class="select-loader"
                        aria-live="polite"
                        ><i class="select-spinner"></i>Loading</span
                      ></label
                    ><select
                      v-model.number="form.classAppliedId"
                      required
                      :disabled="classLoading"
                      :aria-busy="classLoading"
                    >
                      <option value="">
                        {{ classLoading ? "Loading classes…" : "Select class" }}
                      </option>
                      <option
                        v-for="item in classItems"
                        :key="item.Id"
                        :value="Number(item.Id)"
                      >
                        {{
                          item.ClassOption ||
                          item.ClassName ||
                          item.SchClass ||
                          item.Sch_Class ||
                          item.Name
                        }}
                      </option>
                    </select>
                  </div>
                  <div class="field">
                    <label>Admission Type</label
                    ><select v-model="form.admissionType">
                      <option value="">Select admission type</option>
                      <option
                        v-if="
                          form.admissionType &&
                          !admissionTypes.includes(form.admissionType)
                        "
                        :value="form.admissionType"
                      >
                        {{ form.admissionType }}
                      </option>
                      <option
                        v-for="type in admissionTypes"
                        :key="type"
                        :value="type"
                      >
                        {{ type }}
                      </option>
                    </select>
                  </div>
                  <div class="field">
                    <label>Campus</label
                    ><input
                      v-model.trim="form.campus"
                      placeholder="Enter campus or branch"
                    />
                  </div>
                  <div class="field">
                    <label>Residence Type</label
                    ><select v-model="form.residenceType">
                      <option value="">Select residence type</option>
                      <option
                        v-if="
                          form.residenceType &&
                          !residenceTypes.includes(form.residenceType)
                        "
                        :value="form.residenceType"
                      >
                        {{ form.residenceType }}
                      </option>
                      <option
                        v-for="type in residenceTypes"
                        :key="type"
                        :value="type"
                      >
                        {{ type }}
                      </option>
                    </select>
                  </div>
                  <div class="field">
                    <label>Preferred Resumption Date</label
                    ><input
                      v-model="form.preferredResumptionDate"
                      type="date"
                    />
                  </div>
                  <div class="field">
                    <label>Admission Status</label
                    ><select v-model="form.admissionStatus">
                      <option value="">Select admission status</option>
                      <option
                        v-if="
                          form.admissionStatus &&
                          !admissionStatuses.includes(form.admissionStatus)
                        "
                        :value="form.admissionStatus"
                      >
                        {{ form.admissionStatus }}
                      </option>
                      <option
                        v-for="status in admissionStatuses"
                        :key="status"
                        :value="status"
                      >
                        {{ status }}
                      </option>
                    </select>
                  </div>
                </div>
                <div v-else-if="currentStage === 1" class="fields">
                  <div class="field">
                    <label>Surname <b>*</b></label
                    ><input v-model.trim="form.surname" required />
                  </div>
                  <div class="field">
                    <label>First Name <b>*</b></label
                    ><input v-model.trim="form.firstName" required />
                  </div>
                  <div class="field">
                    <label>Middle Name</label
                    ><input v-model.trim="form.middleName" />
                  </div>
                  <div class="field">
                    <label>Date of Birth <b>*</b></label
                    ><input v-model="form.dateOfBirth" type="date" required />
                  </div>
                  <div class="field">
                    <label>Gender <b>*</b></label
                    ><select v-model="form.gender" required>
                      <option value="">Select gender</option>
                      <option>Female</option>
                      <option>Male</option>
                      <option>Other</option>
                    </select>
                  </div>
                  <div class="field">
                    <label>Nationality</label
                    ><input v-model.trim="form.nationality" />
                  </div>
                  <div class="field">
                    <label>State of Origin</label
                    ><input v-model.trim="form.stateOfOrigin" />
                  </div>
                  <div class="field">
                    <label>Local Government Area</label
                    ><input v-model.trim="form.localGovernmentArea" />
                  </div>
                  <div class="field">
                    <label>Applicant Email</label
                    ><input v-model.trim="form.applicantEmail" type="email" />
                  </div>
                  <div class="field full">
                    <label>Residential Address</label
                    ><textarea
                      v-model.trim="form.residentialAddress"
                      rows="2"
                    ></textarea>
                  </div>
                  <div class="field full">
                    <label>Passport Photo</label>
                    <div class="photo-upload">
                      <img
                        class="photo-preview"
                        :src="
                          imageUrl(form.passportPhotoPath) ||
                          '/img/brand/empty.png'
                        "
                        alt="Applicant passport photo preview"
                      />
                      <div class="photo-uploader">
                        <ImageUploader
                          :existingPath="form.passportPhotoPath || ''"
                          @emitOutImgPathval="updatePassportPhoto"
                        />
                        <small v-if="form.passportPhotoPath"
                          >Photo uploaded. Save the application to keep this
                          path on the record.</small
                        >
                      </div>
                    </div>
                  </div>
                </div>
                <div v-else-if="currentStage === 2" class="fields">
                  <div class="field">
                    <label>Known Allergies</label
                    ><textarea v-model.trim="form.knownAllergies"></textarea>
                  </div>
                  <div class="field">
                    <label>Medical Needs</label
                    ><textarea v-model.trim="form.medicalNeeds"></textarea>
                  </div>
                  <div class="field">
                    <label>Dietary Requirements</label
                    ><input v-model.trim="form.dietaryRequirements" />
                  </div>
                  <div class="field">
                    <label>Doctor Contact</label
                    ><input v-model.trim="form.doctorContact" />
                  </div>
                </div>
                <div v-else class="fields">
                  <div class="field">
                    <label>Referral Source</label
                    ><input v-model.trim="form.referralSource" />
                  </div>
                  <div class="field checks">
                    <label
                      ><input
                        v-model="form.declarationAccepted"
                        type="checkbox"
                      />
                      Declaration accepted <b>*</b></label
                    ><label
                      ><input
                        v-model="form.policyConsentAccepted"
                        type="checkbox"
                      />
                      Policy consent accepted <b>*</b></label
                    >
                  </div>
                </div>
                <p v-if="formError" class="alert alert-danger" role="alert">
                  {{ formError }}
                </p>
                <div class="form-actions wizard-actions">
                  <button
                    v-if="currentStage > 0"
                    type="button"
                    class="btn btn-outline-secondary"
                    @click="previousStage"
                    :disabled="saving"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    class="btn btn-light"
                    @click="cancelForm"
                    :disabled="saving"
                  >
                    Cancel
                  </button>
                  <span class="action-spacer"></span>
                  <button
                    v-if="currentStage < stages.length - 1"
                    type="button"
                    class="btn btn-primary"
                    @click="nextStage"
                    :disabled="saving"
                  >
                    Next <i class="fa fa-arrow-right ml-1"></i>
                  </button>
                  <template v-else>
                    <button
                      type="button"
                      class="btn btn-light"
                      @click="resetForm"
                      :disabled="saving"
                    >
                      Reset
                    </button>
                    <button
                      class="btn btn-primary"
                      type="submit"
                      :disabled="saving"
                    >
                      {{
                        saving
                          ? "Saving…"
                          : editingId
                          ? "Update application"
                          : "Create application"
                      }}
                    </button>
                  </template>
                </div>
              </section>
            </div>
          </form>
        </div>
      </modal>
    </main>
  </div>
</template>

<script>
import axios from "axios";
import PageSpinner from "@/components/PageSpinner";
import ImageUploader from "@/components/ImageUploader";
import Alert from "@/mixins/Alert.mixins";

const blankForm = () => ({
  registeredTermId: 0,
  surname: "",
  firstName: "",
  middleName: "",
  dateOfBirth: "",
  gender: "",
  nationality: "Nigerian",
  stateOfOrigin: "",
  localGovernmentArea: "",
  residentialAddress: "",
  passportPhotoPath: "",
  applicantEmail: "",
  knownAllergies: "",
  medicalNeeds: "",
  dietaryRequirements: "",
  doctorContact: "",
  campus: "",
  classAppliedId: 0,
  admissionType: "",
  residenceType: "",
  preferredResumptionDate: "",
  referralSource: "",
  admissionStatus: "",
  declarationAccepted: false,
  policyConsentAccepted: false,
});
const dateInput = (value) => (value ? String(value).slice(0, 10) : "");
const isoDate = (value) =>
  value ? new Date(`${value}T00:00:00.000Z`).toISOString() : null;

export default {
  name: "Admission",
  components: { PageSpinner, ImageUploader },
  mixins: [Alert],
  data() {
    return {
      onloader: "none",
      registeredTerms: [],
      classItems: [],
      applications: [],
      searchQuery: "",
      admissionTypes: ["New admission", "Transfer", "Returning student"],
      admissionStatuses: [
        "Pending",
        "Under review",
        "Admitted",
        "Waitlisted",
        "Rejected",
        "Withdrawn",
      ],
      residenceTypes: ["Day", "Boarding", "Day and boarding"],
      selectedTermId: 0,
      lastLoadedTermId: 0,
      termsLoading: false,
      classLoading: false,
      listLoading: false,
      formVisible: false,
      saving: false,
      editingId: 0,
      currentStage: 0,
      stages: [
        {
          title: "Academic placement",
          summary: "Term and class",
          description:
            "Choose the admission term and intended class, then add placement details.",
        },
        {
          title: "Applicant details",
          summary: "Personal information",
          description:
            "Enter the applicant's identity and contact information.",
        },
        {
          title: "Medical information",
          summary: "Optional care details",
          description:
            "Add any health, dietary, or care information the school should know.",
        },
        {
          title: "Consent and review",
          summary: "Referral and consent",
          description:
            "Provide a referral source and confirm the required declarations.",
        },
      ],
      form: blankForm(),
      listError: "",
      formError: "",
    };
  },
  computed: {
    filteredApplications() {
      const query = this.searchQuery.toLowerCase();
      if (!query) return this.applications;
      return this.applications.filter((row) =>
        [
          row.applicationReference,
          row.firstName,
          row.middleName,
          row.surname,
          row.applicantEmail,
          row.gender,
          row.className,
          row.sessionName,
          row.termName,
          row.admissionType,
          row.admissionStatus,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(query)
      );
    },
    selectedTermLabel() {
      const term = this.registeredTerms.find(
        (item) => Number(item.registeredTermId) === Number(this.selectedTermId)
      );
      return term
        ? `${term.schSession || ""}${term.schTerm ? ` - ${term.schTerm}` : ""}`
        : "";
    },
  },
  watch: {
    selectedTermId(value, previous) {
      if (Number(value) !== Number(previous)) {
        this.searchQuery = "";
        this.lastLoadedTermId = 0;
        this.loadApplications();
      }
    },
  },
  methods: {
    statusClass(status) {
      const value = String(status || "pending").toLowerCase();
      if (value.includes("admitted") || value.includes("accepted"))
        return "status-success";
      if (value.includes("reject") || value.includes("withdraw"))
        return "status-danger";
      if (value.includes("wait")) return "status-warning";
      if (value.includes("review")) return "status-info";
      return "status-neutral";
    },
    async loadOptions() {
      this.termsLoading = true;
      this.classLoading = true;
      try {
        const [terms, classes] = await Promise.all([
          this.$store.dispatch(
            "PortalSchoolSessionTerm/GetPortalRegisteredTerm",
            { SearchValue: null, PageNumber: 1, PageSize: 100 }
          ),
          this.$store.dispatch("getclass"),
        ]);
        this.registeredTerms = Array.isArray(terms && terms.data)
          ? terms.data
          : [];
        this.classItems = Array.isArray(classes) ? classes : [];
      } catch (error) {
        this.ShowAlertError(
          this.errorText(error, "Could not load registered terms or classes."),
          ""
        );
      } finally {
        this.termsLoading = false;
        this.classLoading = false;
      }
    },
    async loadApplications(force = false) {
      const termId = Number(this.selectedTermId);
      if (!Number.isInteger(termId) || termId <= 0) {
        this.applications = [];
        this.lastLoadedTermId = 0;
        return;
      }
      if (!force && this.lastLoadedTermId === termId) return;
      this.listLoading = true;
      this.listError = "";
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_ENV_API_URL}AdmissionApplication/GetAdmissionApplications?RegisteredTermId=${termId}`,
          {
            headers: {
              Authorization: `bearer ${this.$store.state.auth.token}`,
            },
          }
        );
        const body = response.data || {};
        if (body.responseCode !== "00")
          throw new Error(
            body.responseMessage || "Could not retrieve admission applications."
          );
        this.applications = Array.isArray(body.data) ? body.data : [];
        this.lastLoadedTermId = termId;
      } catch (error) {
        this.applications = [];
        this.listError = this.errorText(
          error,
          "Could not load admission applications."
        );
      } finally {
        this.listLoading = false;
      }
    },
    startCreate() {
      this.editingId = 0;
      this.currentStage = 0;
      this.form = blankForm();
      this.form.registeredTermId = Number(this.selectedTermId) || 0;
      this.formVisible = true;
      this.formError = "";
    },
    editApplication(row) {
      this.editingId = Number(row.admissionApplicationId);
      this.currentStage = 0;
      this.form = {
        ...blankForm(),
        ...row,
        registeredTermId: Number(row.registeredTermId) || 0,
        classAppliedId: Number(row.classAppliedId) || 0,
        passportPhotoPath: row.passportPhotoPath || row.PassportPhotoPath || "",
        dateOfBirth: dateInput(row.dateOfBirth),
        preferredResumptionDate: dateInput(row.preferredResumptionDate),
        declarationAccepted: Boolean(row.declarationAccepted),
        policyConsentAccepted: Boolean(row.policyConsentAccepted),
        middleName: row.middleName || "",
      };
      this.formVisible = true;
      this.formError = "";
    },
    cancelForm() {
      this.formVisible = false;
      this.formError = "";
      this.currentStage = 0;
    },
    nextStage() {
      const form = this.$refs.admissionForm;
      if (!form || !form.checkValidity()) {
        if (form) form.reportValidity();
        return;
      }
      this.formError = "";
      this.currentStage = Math.min(
        this.currentStage + 1,
        this.stages.length - 1
      );
    },
    previousStage() {
      this.formError = "";
      this.currentStage = Math.max(0, this.currentStage - 1);
    },
    goToStage(index) {
      if (index <= this.currentStage) {
        this.formError = "";
        this.currentStage = index;
      }
    },
    updatePassportPhoto(path) {
      this.form.passportPhotoPath = path || "";
    },
    imageUrl(path) {
      if (!path) return "";
      if (/^(https?:|data:|blob:)/i.test(path)) return path;
      const baseUrl = this.$store.state.auth.baseurl || "";
      const normalizedPath = String(path).replace(/^\/+/, "");
      return `${baseUrl.replace(/\/+$/, "")}/${normalizedPath}`;
    },
    resetForm() {
      if (this.editingId)
        this.editApplication(
          this.applications.find(
            (row) => Number(row.admissionApplicationId) === this.editingId
          ) || {}
        );
      else this.startCreate();
    },
    async submitApplication() {
      if (this.saving) return;
      const f = this.form;
      if (
        !(Number(f.registeredTermId) > 0) ||
        !(Number(f.classAppliedId) > 0) ||
        !f.surname ||
        !f.firstName ||
        !f.dateOfBirth ||
        !f.gender ||
        !f.declarationAccepted ||
        !f.policyConsentAccepted
      ) {
        this.formError =
          "Complete the required fields, select a registered term and class, and accept both declarations.";
        return;
      }
      if (f.applicantEmail && !/^\S+@\S+\.\S+$/.test(f.applicantEmail)) {
        this.formError = "Enter a valid applicant email address.";
        return;
      }
      this.saving = true;
      this.formError = "";
      const payload = {
        registeredTermId: Number(f.registeredTermId),
        surname: f.surname,
        firstName: f.firstName,
        middleName: f.middleName || null,
        dateOfBirth: isoDate(f.dateOfBirth),
        gender: f.gender,
        nationality: f.nationality || null,
        stateOfOrigin: f.stateOfOrigin || null,
        localGovernmentArea: f.localGovernmentArea || null,
        residentialAddress: f.residentialAddress || null,
        passportPhotoPath: f.passportPhotoPath || null,
        applicantEmail: f.applicantEmail || null,
        knownAllergies: f.knownAllergies || null,
        medicalNeeds: f.medicalNeeds || null,
        dietaryRequirements: f.dietaryRequirements || null,
        doctorContact: f.doctorContact || null,
        campus: f.campus || null,
        classAppliedId: Number(f.classAppliedId),
        admissionType: f.admissionType || null,
        residenceType: f.residenceType || null,
        preferredResumptionDate: isoDate(f.preferredResumptionDate),
        referralSource: f.referralSource || null,
        admissionStatus: f.admissionStatus || null,
        declarationAccepted: Boolean(f.declarationAccepted),
        policyConsentAccepted: Boolean(f.policyConsentAccepted),
      };
      if (this.editingId) payload.admissionApplicationId = this.editingId;
      try {
        const operation = this.editingId
          ? "UpdateAdmissionApplication"
          : "CreateAdmissionApplication";
        const response = await axios.post(
          `${process.env.VUE_APP_ENV_API_URL}AdmissionApplication/${operation}`,
          payload,
          {
            headers: {
              Authorization: `bearer ${this.$store.state.auth.token}`,
            },
          }
        );
        const body = response.data || {};
        if (body.responseCode !== "00")
          throw new Error(
            body.responseMessage ||
              "The admission application could not be saved."
          );
        this.ShowAlertSuccess(
          body.responseMessage ||
            (this.editingId
              ? "Admission application updated."
              : "Admission application created."),
          ""
        );
        this.formVisible = false;
        this.currentStage = 0;
        this.form = blankForm();
        await this.loadApplications(true);
      } catch (error) {
        this.formError = this.errorText(
          error,
          "The admission application could not be saved."
        );
        this.ShowAlertError(this.formError, "");
      } finally {
        this.saving = false;
      }
    },
    formatDate(value) {
      if (!value) return "—";
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? "—" : date.toLocaleDateString();
    },
    errorText(error, fallback) {
      return (
        (error &&
          error.response &&
          error.response.data &&
          (error.response.data.responseMessage || error.response.data)) ||
        (error && error.message) ||
        fallback
      );
    },
  },
  mounted() {
    this.loadOptions();
  },
};
</script>

<style scoped>
.admission-shell {
  --admission-ink: #24364b;
  --admission-muted: #738196;
  --admission-line: #e5ebf1;
  max-width: 1600px;
  padding-bottom: 48px;
  min-width: 0;
}
.page-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 22px;
  color: #fff;
  min-width: 0;
}
.page-heading h1 {
  margin: 5px 0;
  font-size: clamp(22px, 2.4vw, 30px);
  font-weight: 700;
  color: #fff;
}
.page-heading p {
  margin: 0;
  color: rgba(255, 255, 255, 0.82);
}
.page-heading > .btn {
  min-height: 44px;
  padding: 10px 16px;
  border: 0;
  border-radius: 7px;
  color: #245b8e;
  font-weight: 650;
  box-shadow: 0 3px 12px rgba(15, 42, 67, 0.15);
}
.eyebrow {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1.2px;
  color: #3078ae;
}
.panel {
  background: #fff;
  border: 1px solid var(--admission-line);
  border-radius: 14px;
  box-shadow: 0 10px 32px rgba(29, 46, 69, 0.055);
  padding: 22px;
}
.toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}
.toolbar label {
  margin: 0;
  color: #34445a;
  font-size: 13px;
  font-weight: 650;
}
.select-loader {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: 8px;
  color: #6f7f93;
  font-size: 11px;
  font-weight: 500;
}
.select-spinner {
  display: inline-block;
  width: 12px;
  height: 12px;
  border: 2px solid #d7e0e9;
  border-top-color: #347eb2;
  border-radius: 50%;
  animation: select-spin 0.7s linear infinite;
}
@keyframes select-spin {
  to {
    transform: rotate(360deg);
  }
}
.toolbar select {
  min-width: 270px;
  min-height: 42px;
  padding: 9px 11px;
  border: 1px solid #dce2e8;
  border-radius: 6px;
  color: #253247;
  background: #fff;
}
.record-tools {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 14px;
  flex: 1 1 260px;
  min-width: 0;
}
.application-search {
  width: min(100%, 300px);
  min-height: 42px;
  padding: 9px 12px;
  border: 1px solid #dce2e8;
  border-radius: 8px;
  color: var(--admission-ink);
  background: #fff;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.application-search:focus {
  outline: 0;
  border-color: #4b83bd;
  box-shadow: 0 0 0 3px rgba(37, 99, 166, 0.11);
}
.application-search:disabled {
  background: #f7f9fb;
}
.loading-label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--admission-muted);
  white-space: nowrap;
}
.record-count {
  white-space: nowrap;
  font-size: 12px;
}
.muted,
.empty {
  color: #738095;
}
.loading-state {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
}
.empty {
  padding: 36px 20px;
  text-align: center;
  line-height: 1.6;
}
.application-list {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 320px), 1fr));
  gap: 14px;
  min-width: 0;
}
.application-card {
  min-width: 0;
  padding: 17px;
  border: 1px solid #e5ebf1;
  border-radius: 12px;
  background: #fff;
  transition: border-color 0.16s ease, box-shadow 0.16s ease, transform 0.16s ease;
}
.application-card:hover {
  border-color: #d3e0eb;
  box-shadow: 0 8px 22px rgba(29, 46, 69, 0.07);
  transform: translateY(-1px);
}
.application-card-heading {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  padding-bottom: 14px;
  border-bottom: 1px solid #edf0f3;
}
.applicant-heading-copy {
  flex: 1 1 auto;
  min-width: 0;
}
.application-reference {
  display: block;
  margin-bottom: 3px;
  color: var(--admission-muted);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  overflow-wrap: anywhere;
  text-transform: uppercase;
}
.applicant-heading-copy h2 {
  margin: 0;
  color: var(--admission-ink);
  font-size: 15px;
  font-weight: 700;
  line-height: 1.35;
  overflow-wrap: anywhere;
}
.application-details {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px 12px;
  margin: 0;
  padding: 16px 0;
}
.application-detail {
  min-width: 0;
}
.application-detail dt {
  margin-bottom: 4px;
  color: var(--admission-muted);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.application-detail dd {
  margin: 0;
  color: #39485c;
  font-size: 12px;
  line-height: 1.45;
  overflow-wrap: anywhere;
  word-break: break-word;
}
.application-card-actions {
  display: flex;
  justify-content: flex-end;
  padding-top: 12px;
  border-top: 1px solid #edf0f3;
}
.application-card-actions .btn {
  min-height: 38px;
  padding-right: 14px;
  padding-left: 14px;
}
.status-badge {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  padding: 5px 9px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
}
.status-success { color: #236b4d; background: #e7f5ed; }
.status-danger { color: #9b3d48; background: #fae9eb; }
.status-warning { color: #855c1d; background: #fbf2dc; }
.status-info { color: #285d83; background: #e8f2fa; }
.status-neutral { color: #5e6d80; background: #edf1f5; }
.applicant-photo {
  width: 42px;
  height: 42px;
  border: 1px solid #e0e6ed;
  border-radius: 50%;
  background: #f6f8fa;
  object-fit: cover;
}
.photo-upload {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}
.photo-preview {
  flex: 0 0 96px;
  width: 96px;
  height: 112px;
  border: 1px solid #dce3ea;
  border-radius: 8px;
  background: #f6f8fa;
  object-fit: cover;
}
.photo-uploader {
  min-width: 0;
  flex: 1;
}
.photo-uploader >>> .dropzone-container {
  padding: 14px;
  border-radius: 8px;
}
.photo-uploader >>> .file-label {
  font-size: 13px;
}
.photo-uploader > small {
  display: block;
  margin-top: 6px;
  color: #64748b;
  font-size: 11px;
}
.panel-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}
.panel-heading h2 {
  margin: 5px 0;
  color: #26364b;
  font-size: 20px;
  font-weight: 700;
}
.form-panel form {
  max-width: 1100px;
}
.wizard-layout {
  display: grid;
  grid-template-columns: minmax(190px, 230px) minmax(0, 1fr);
  gap: 28px;
  align-items: start;
}
.wizard-sidebar {
  position: sticky;
  top: 12px;
  display: grid;
  gap: 7px;
  padding: 14px 12px;
  border: 1px solid #e8edf2;
  border-radius: 10px;
  background: #f8fafc;
}
.wizard-label {
  margin: 0 6px 5px;
  color: #8895a5;
  font-size: 10px;
  font-weight: 750;
  letter-spacing: 0.1em;
}
.wizard-step {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  width: 100%;
  padding: 10px 8px;
  border: 0;
  border-radius: 7px;
  background: transparent;
  color: #617187;
  text-align: left;
  cursor: pointer;
}
.wizard-step:disabled {
  cursor: default;
}
.wizard-step.active {
  background: #eaf2fa;
  color: #245b8e;
}
.wizard-step.complete .step-number {
  background: #2e8b68;
  color: #fff;
}
.step-number {
  display: grid;
  flex: 0 0 25px;
  width: 25px;
  height: 25px;
  place-items: center;
  border-radius: 50%;
  background: #e7ecf1;
  color: #607187;
  font-size: 11px;
  font-weight: 700;
}
.wizard-step.active .step-number {
  background: #2f73a8;
  color: #fff;
}
.step-copy {
  display: grid;
  gap: 2px;
  padding-top: 2px;
}
.step-copy strong {
  font-size: 12px;
  font-weight: 700;
}
.step-copy small {
  color: #8693a3;
  font-size: 10px;
}
.wizard-stage {
  min-width: 0;
}
.stage-heading {
  margin-bottom: 22px;
  padding-bottom: 15px;
  border-bottom: 1px solid #edf0f3;
}
.stage-heading h3 {
  margin: 5px 0 3px;
  color: #2f4055;
  font-size: 19px;
  font-weight: 700;
}
.stage-heading p {
  margin: 0;
  color: #7c8999;
  font-size: 12px;
}
.form-panel h3 {
  margin: 26px 0 15px;
  padding: 0 0 10px;
  border-bottom: 1px solid #e9edf1;
  color: #2f4055;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.25px;
}
.form-panel h3:first-child {
  margin-top: 0;
}
.form-panel h3 small {
  color: #8793a4;
  font-size: 11px;
  font-weight: 400;
}
.fields {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px 22px;
}
.field {
  min-width: 0;
}
.field.full {
  grid-column: 1/-1;
}
.field label {
  display: block;
  margin-bottom: 7px;
  color: #465469;
  font-size: 12px;
  font-weight: 650;
}
.field label b {
  color: #cf5961;
}
.field input:not([type="checkbox"]),
.field select,
.field textarea {
  width: 100%;
  min-height: 44px;
  padding: 9px 11px;
  border: 1px solid #dce2e8;
  border-radius: 6px;
  background: #fff;
  color: #253247;
  font: inherit;
  font-size: 13px;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.field input:focus,
.field select:focus,
.field textarea:focus,
.toolbar select:focus {
  outline: 0;
  border-color: #4b83bd;
  box-shadow: 0 0 0 3px rgba(37, 99, 166, 0.11);
}
.field textarea {
  resize: vertical;
}
.field input[type="file"] {
  width: 100%;
  padding: 7px;
  border: 1px dashed #cfd8e1;
  border-radius: 6px;
  color: #65748a;
  font-size: 12px;
}
.field small {
  display: block;
  margin-top: 6px;
  color: #7d8999;
  font-size: 11px;
  overflow-wrap: anywhere;
}
.checks {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}
.checks label {
  display: flex;
  align-items: center;
  gap: 7px;
  margin: 0;
}
.checks input {
  accent-color: #2563a6;
}
.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 9px;
  margin-top: 26px;
  padding-top: 18px;
  border-top: 1px solid #edf0f3;
}
.wizard-actions {
  align-items: center;
}
.action-spacer {
  flex: 1;
}
.form-actions .btn {
  min-width: 92px;
  min-height: 42px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 650;
}
.form-actions .btn-primary {
  background: #2563a6;
  border-color: #2563a6;
}
.alert {
  font-size: 13px;
}
button,
a,
input,
select,
textarea {
  -webkit-tap-highlight-color: transparent;
}
button:focus-visible,
a:focus-visible {
  outline: 3px solid rgba(37, 99, 166, 0.3);
  outline-offset: 2px;
}
@media (max-width: 650px) {
  .admission-shell {
    padding-right: 12px;
    padding-left: 12px;
  }
  .page-heading {
    align-items: flex-start;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 18px;
  }
  .page-heading > .btn {
    width: 100%;
  }
  .panel {
    padding: 16px;
  }
  .fields {
    grid-template-columns: 1fr;
  }
  .photo-upload {
    flex-direction: column;
  }
  .photo-preview {
    width: 84px;
    height: 96px;
  }
  .wizard-layout {
    grid-template-columns: minmax(0, 1fr);
    gap: 18px;
  }
  .wizard-sidebar {
    position: static;
    display: flex;
    gap: 5px;
    margin: 0 -4px;
    padding: 8px;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
  }
  .wizard-label {
    display: none;
  }
  .wizard-step {
    flex: 0 0 auto;
    width: auto;
    min-width: 145px;
    align-items: center;
    scroll-snap-align: start;
  }
  .step-copy small {
    display: none;
  }
  .stage-heading {
    margin-bottom: 16px;
  }
  .field.full {
    grid-column: auto;
  }
  .toolbar {
    align-items: stretch;
    flex-direction: column;
  }
  .toolbar label {
    display: block;
  }
  .toolbar select {
    width: 100%;
    min-width: 0;
  }
  .record-tools {
    flex: auto;
    align-items: stretch;
    flex-direction: column;
    gap: 8px;
  }
  .application-search {
    width: 100%;
  }
  .record-count {
    align-self: flex-end;
  }
  .application-list {
    grid-template-columns: minmax(0, 1fr);
    gap: 10px;
  }
  .application-card {
    padding: 14px;
  }
  .application-card-heading {
    align-items: flex-start;
    gap: 10px;
  }
  .application-card-heading .status-badge {
    flex: 0 0 auto;
    white-space: normal;
    text-align: center;
  }
  .application-details {
    gap: 12px 10px;
  }
  .form-actions {
    flex-wrap: wrap;
  }
  .form-actions .btn {
    flex: 1;
  }
  .wizard-actions .action-spacer {
    display: none;
  }
  .wizard-actions {
    justify-content: stretch;
  }
}
@media (max-width: 420px) {
  .panel {
    padding: 13px;
    border-radius: 11px;
  }
  .application-card-heading {
    flex-wrap: wrap;
  }
  .application-card-heading .applicant-photo {
    flex: 0 0 40px;
  }
  .application-card-heading .status-badge {
    margin-left: 50px;
  }
  .application-details {
    grid-template-columns: minmax(0, 1fr);
    gap: 11px;
  }
  .form-actions .btn {
    min-width: 0;
  }
}
@media (max-height: 520px) and (orientation: landscape) {
  .wizard-sidebar {
    position: static;
  }
}
</style>
